import React, { useCallback, useEffect, useState } from "react";
import api, { portalJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import { RefreshCw, Zap, CheckCheck, CloudDownload, FileJson, Upload, ListChecks } from "lucide-react";
import { PortalLogin, EvcFile } from "@/components/gst/PortalLogin";
import { Gstr1PortalCheck } from "@/components/gst/Gstr1PortalCheck";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const periodOptions = () => {
  const d = new Date(); const out = [];
  for (let i = 0; i < 24; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ v: String(x.getMonth() + 1).padStart(2, "0") + x.getFullYear(), l: `${MONTHS[x.getMonth()]} ${x.getFullYear()}` });
  }
  return out;
};

const Stat = ({ k, v, tone = "", testId }) => (
  <div className="border rounded-sm px-3 py-2 bg-white" data-testid={testId}>
    <div className="text-[10px] font-mono uppercase text-muted-foreground">{k}</div>
    <div className={`text-lg font-semibold tabular-nums ${tone}`}>{v}</div>
  </div>
);

function IrnTab({ rows, period, reload }) {
  const [busy, setBusy] = useState(false);
  const need = rows.filter((r) => r.irn_needed);
  const push = async (ids) => {
    setBusy(true);
    try {
      const r = await api.post(`/gstr1/workbench/${period}/push-irn`, { invoice_ids: ids });
      const fails = r.data.results.filter((x) => !x.ok);
      toast[fails.length ? "warning" : "success"](`${r.data.done} IRN generated${fails.length ? ` · ${fails.length} failed: ${fails[0].error}` : ""}`, { duration: 9000 });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "IRN push failed"); }
    setBusy(false);
  };
  if (!need.length) return <div className="text-sm text-emerald-700 p-4" data-testid="irn-all-ok">Every invoice that needs an e-invoice already has an IRN.</div>;
  const pushable = need.filter((r) => r.irn_can_push).map((r) => r.invoice_id);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm">{need.length} invoice(s) need an IRN · {pushable.length} still within the e-invoice portal deadline</div>
        <Button size="sm" disabled={busy || !pushable.length} onClick={() => push(pushable)} className="gap-1.5 bg-accent text-white" data-testid="irn-push-all">
          <Zap className="w-3.5 h-3.5" /> Push {pushable.length} to e-invoice portal</Button>
      </div>
      <table className="w-full dense text-sm"><thead><tr><th>Invoice</th><th>Date</th><th>Customer</th><th className="text-right">Value</th><th>Deadline</th><th></th></tr></thead>
        <tbody>{need.map((r) => (
          <tr key={r.invoice_id} data-testid={`irn-row-${r.invoice_number}`}>
            <td className="font-mono text-xs">{r.invoice_number}</td><td>{r.date}</td><td>{r.customer_name}</td>
            <td className="text-right tabular-nums">{fmtINR(r.grand_total)}</td>
            <td className={r.irn_expired ? "text-red-700 text-xs" : "text-xs"}>{r.irn_expired ? `Expired ${r.irn_deadline} — cannot be reported` : (r.irn_deadline || "No limit")}</td>
            <td className="text-right">{r.irn_can_push && <Button size="sm" variant="outline" disabled={busy} onClick={() => push([r.invoice_id])} data-testid={`irn-push-${r.invoice_number}`}>Push</Button>}</td>
          </tr>))}</tbody></table>
    </div>
  );
}

function ApproveTab({ rows, period, reload, synced }) {
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const eligible = rows.filter((r) => !r.irn_needed && r.section !== "B2CS");
  const act = async (approved, ids) => {
    try { await api.post(`/gstr1/workbench/${period}/approve`, { invoice_ids: ids, approved }); setSel(new Set()); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sync = async () => {
    setBusy(true);
    try { const r = await api.post(`/gstr1/workbench/${period}/portal-sync`); toast.success(`${r.data.on_portal} invoices already on the GST portal`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Portal read failed", { duration: 9000 }); }
    setBusy(false);
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={sync} disabled={busy} className="gap-1.5" data-testid="gstr1-portal-sync"><CloudDownload className="w-3.5 h-3.5" /> {busy ? "Reading portal…" : "Check what's already on the portal"}</Button>
        <span className="text-xs text-muted-foreground" data-testid="gstr1-portal-synced">{synced ? `Portal checked ${new Date(synced).toLocaleString("en-IN")}` : "Portal not checked yet"}</span>
        <div className="flex-1" />
        <Button size="sm" disabled={!sel.size} onClick={() => act(true, [...sel])} className="gap-1.5 bg-accent text-white" data-testid="gstr1-approve-selected"><CheckCheck className="w-3.5 h-3.5" /> Approve {sel.size || ""}</Button>
        <Button size="sm" variant="outline" disabled={!sel.size} onClick={() => act(false, [...sel])} data-testid="gstr1-unapprove-selected">Remove approval</Button>
      </div>
      <div className="text-[11px] text-muted-foreground">B2C small (B2CS) invoices are reported as state-wise totals automatically. Invoices still waiting for an IRN appear after the IRN is generated.</div>
      <table className="w-full dense text-sm"><thead><tr>
        <th><input type="checkbox" checked={sel.size === eligible.length && eligible.length > 0} onChange={(e) => setSel(e.target.checked ? new Set(eligible.map((r) => r.invoice_id)) : new Set())} data-testid="gstr1-select-all" /></th>
        <th>Invoice</th><th>Section</th><th>Customer</th><th className="text-right">Taxable</th><th className="text-right">Tax</th><th>Status</th></tr></thead>
        <tbody>{eligible.map((r) => (
          <tr key={r.invoice_id} data-testid={`gstr1-row-${r.invoice_number}`}>
            <td><input type="checkbox" checked={sel.has(r.invoice_id)} onChange={() => toggle(r.invoice_id)} data-testid={`gstr1-check-${r.invoice_number}`} /></td>
            <td className="font-mono text-xs">{r.invoice_number}</td><td className="text-xs">{r.section}</td><td>{r.customer_name}</td>
            <td className="text-right tabular-nums">{fmtINR(r.taxable)}</td><td className="text-right tabular-nums">{fmtINR(r.cgst + r.sgst + r.igst)}</td>
            <td className="text-xs" data-testid={`gstr1-status-${r.invoice_number}`}>{r.on_portal ? <span className="text-sky-700">Already on portal</span> : r.approved ? <span className="text-emerald-700">Approved · will be pushed</span> : <span className="text-muted-foreground">Awaiting approval</span>}</td>
          </tr>))}</tbody></table>
    </div>
  );
}

function TablesTab({ period }) {
  const [t, setT] = useState(null);
  useEffect(() => { api.get(`/gstr1/workbench/${period}/tables`).then((r) => setT(r.data)).catch(() => setT({ hsn: { b2b: [], b2c: [] }, docs: [] })); }, [period]);
  if (!t) return <div className="p-4 text-sm">Loading…</div>;
  const Hsn = ({ title, rows, testId }) => (
    <div data-testid={testId}><div className="text-sm font-medium mb-1">{title}</div>
      <table className="w-full dense text-xs"><thead><tr><th>HSN/SAC</th><th>Description</th><th>UQC</th><th className="text-right">Qty</th><th className="text-right">Rate %</th><th className="text-right">Taxable</th><th className="text-right">IGST</th><th className="text-right">CGST</th><th className="text-right">SGST</th></tr></thead>
        <tbody>{rows.length ? rows.map((h) => <tr key={`${h.hsn_sc}-${h.rt}-${h.uqc}`}><td className="font-mono">{h.hsn_sc}</td><td>{h.desc}</td><td>{h.uqc}</td><td className="text-right">{h.qty}</td><td className="text-right">{h.rt}</td><td className="text-right">{fmtINR(h.txval)}</td><td className="text-right">{fmtINR(h.iamt)}</td><td className="text-right">{fmtINR(h.camt)}</td><td className="text-right">{fmtINR(h.samt)}</td></tr>)
          : <tr><td colSpan={9} className="text-muted-foreground">No rows</td></tr>}</tbody></table></div>
  );
  return (
    <div className="space-y-5">
      <Hsn title="Table 12 · HSN summary — B2B" rows={t.hsn.b2b} testId="hsn-b2b" />
      <Hsn title="Table 12 · HSN summary — B2C" rows={t.hsn.b2c} testId="hsn-b2c" />
      <div data-testid="doc-table"><div className="text-sm font-medium mb-1">Table 13 · Documents issued</div>
        <table className="w-full dense text-xs"><thead><tr><th>Document type</th><th>From</th><th>To</th><th className="text-right">Total</th><th className="text-right">Cancelled</th><th className="text-right">Net issued</th></tr></thead>
          <tbody>{t.docs.flatMap((d) => d.docs.map((x) => <tr key={`${d.doc_num}-${x.num}`}><td>{d.doc_typ}</td><td className="font-mono">{x.from}</td><td className="font-mono">{x.to}</td><td className="text-right">{x.totnum}</td><td className="text-right">{x.cancel}</td><td className="text-right">{x.net_issue}</td></tr>))}</tbody></table></div>
    </div>
  );
}

const COOL_S = 60;
function useSaveCooldown(period) {
  const key = `g1-saved-${period}`;
  const calc = useCallback(() => Math.max(0, COOL_S - Math.floor((Date.now() - Number(localStorage.getItem(key) || 0)) / 1000)), [key]);
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    setLeft(calc());
    const t = setInterval(() => {
      setLeft((prev) => { const n = calc(); if (prev > 0 && n === 0) toast.info("gst.gov.in has had a minute to process the save — you can generate the summary now.", { duration: 10000 }); return n; });
    }, 1000);
    return () => clearInterval(t);
  }, [calc]);
  const start = () => { localStorage.setItem(key, String(Date.now())); setLeft(COOL_S); };
  return [left, start];
}

function FileTab({ period, toPush, reload }) {
  const [loggedIn, setLoggedIn] = useState(false);
  const [cool, startCool] = useSaveCooldown(period);
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState(null);
  const [fail, setFail] = useState(null);
  const [loginKey, setLoginKey] = useState(0);
  const step = async (k, fn) => {
    setBusy(k); setFail(null);
    try { await fn(); } catch (e) {
      const d = e?.response?.data?.detail; const msg = typeof d === "string" ? d : d?.message || "Failed";
      toast.error(msg, { duration: 12000 });
      setFail({ msg, trail: d?.detail?.trail || [], screenshot: d?.detail?.screenshot || "" });
      if (/log in again|ended this session|login_required/i.test(msg + (d?.error || ""))) setLoginKey((x) => x + 1);
    }
    setBusy("");
  };
  return (
    <div className="space-y-3">
      <PortalLogin key={loginKey} onChange={setLoggedIn} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!loggedIn || !!busy} className="gap-1.5 bg-accent text-white" data-testid="g1-portal-save"
          onClick={() => step("save", async () => { const r = await portalJob(`/gst-portal/gstr1/${period}/save`); setRes(r.data.upload); startCool(); toast.success(`gst.gov.in: ${r.data.upload?.status === "P" ? "processed" : r.data.upload?.status} — Generate summary unlocks in 1 minute`); reload(); })}>
          <Upload className="w-3.5 h-3.5" /> {busy === "save" ? "Saving…" : `1 · Save ${toPush} new invoice(s) + B2CS, HSN & document tables`}</Button>
        <Button size="sm" variant="outline" disabled={!loggedIn || !!busy || cool > 0} className="gap-1.5" data-testid="g1-portal-summary"
          onClick={() => step("sum", async () => { await portalJob(`/gst-portal/gstr1/${period}/summary`); toast.success("Summary generated on gst.gov.in"); })}>
          <ListChecks className="w-3.5 h-3.5" /> {busy === "sum" ? "Generating…" : cool > 0 ? `2 · Generate summary (wait ${cool}s)` : "2 · Generate summary"}</Button>
      </div>
      {cool > 0 && <div className="text-xs text-muted-foreground" data-testid="g1-summary-cooldown">Saved. gst.gov.in needs about a minute to process the upload — Generate summary and Check draft unlock in {cool}s.</div>}
      {res && <div className="text-xs border rounded-sm p-2 space-y-1" data-testid="g1-portal-result">
        <div>Upload status: <b>{{ P: "Processed", PE: "Processed with errors", IP: "Still processing on gst.gov.in — use Check draft on portal in a few minutes" }[res.status] || res.status}</b>{res.ref ? ` · ref ${res.ref}` : ""}</div>
        {res.errors?.length > 0 && <table className="w-full dense" data-testid="g1-portal-errors"><thead><tr><th>Section</th><th>Document</th><th>Error</th></tr></thead>
          <tbody>{res.errors.slice(0, 50).map((x, i) => <tr key={i}><td>{x.section}</td><td className="font-mono">{x.invoice_no}</td><td>{x.error_msg}</td></tr>)}</tbody></table>}
      </div>}
      {fail && <div className="text-xs border border-red-300 bg-red-50 rounded-sm p-2 space-y-1" data-testid="g1-portal-fail">
        <div className="text-red-800 font-medium">{fail.msg}</div>
        {fail.trail.length > 0 && <div className="text-muted-foreground">Steps done on gst.gov.in: {fail.trail.filter((t) => typeof t === "string").join(" → ")}</div>}
        {fail.screenshot && <img src={`data:image/jpeg;base64,${fail.screenshot}`} alt="gst.gov.in at the point it stopped" className="border rounded-sm max-h-[420px] w-auto" data-testid="g1-portal-fail-shot" />}
      </div>}
      <Gstr1PortalCheck period={period} loggedIn={loggedIn && cool === 0} />
      <EvcFile ret="gstr1" period={period} disabled={!loggedIn} onFiled={reload} />
    </div>
  );
}

export default function Gstr1Workbench() {
  const [period, setPeriod] = useState(() => periodOptions()[1].v);
  const [wb, setWb] = useState(null);
  const valid = /^(0[1-9]|1[0-2])\d{4}$/.test(period);
  const load = useCallback(async () => {
    if (!valid) { return; }
    try {
      const r = await api.get(`/gstr1/workbench/${period}`);
      setWb((cur) => (r.data.period === period ? r.data : cur));
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load GSTR-1"); }
  }, [period, valid]);
  useEffect(() => { load(); }, [load]);
  const dlJson = async () => {
    try {
      const r = await api.get(`/gstr1/workbench/${period}/json`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" }));
      a.download = `GSTR1-${period}.json`; a.click();
    } catch (e) { toast.error(e?.response?.data?.detail || "JSON failed"); }
  };
  const s = wb?.summary || {};
  return (
    <div className="space-y-5 p-6" data-testid="gstr1-workbench">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><div className="text-[10px] font-mono uppercase tracking-widest text-accent">// GST · GSTR-1</div>
          <h1 className="text-3xl font-heading">GSTR-1 workbench</h1>
          <div className="text-sm text-muted-foreground">E-invoice check → approve invoices → only new ones go to the portal, with HSN and document tables.</div></div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44" data-testid="gstr1-period"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{periodOptions().map((o) => <SelectItem key={o.v} value={o.v} data-testid={`gstr1-period-${o.v}`}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="gap-1.5" data-testid="gstr1-reload"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Button>
          <Button onClick={dlJson} variant="outline" className="gap-1.5" data-testid="gstr1-json-dl"><FileJson className="w-3.5 h-3.5" /> GSTN JSON</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat k="Invoices" v={wb?.totals?.invoice_count ?? "—"} testId="st-invoices" />
        <Stat k="IRN missing" v={s.irn_missing ?? "—"} tone={s.irn_missing ? "text-amber-700" : ""} testId="st-irn-missing" />
        <Stat k="IRN expired" v={s.irn_expired ?? "—"} tone={s.irn_expired ? "text-red-700" : ""} testId="st-irn-expired" />
        <Stat k="Approved" v={s.approved ?? "—"} testId="st-approved" />
        <Stat k="On portal" v={s.on_portal ?? "—"} testId="st-on-portal" />
        <Stat k="To push" v={s.to_push ?? "—"} tone={s.to_push ? "text-accent" : ""} testId="st-to-push" />
      </div>
      {wb && valid && wb.period === period && (
        <Tabs defaultValue="irn">
          <TabsList><TabsTrigger value="irn" data-testid="tab-irn">1 · E-invoice check</TabsTrigger><TabsTrigger value="approve" data-testid="tab-approve">2 · Approve invoices</TabsTrigger><TabsTrigger value="tables" data-testid="tab-tables">3 · HSN & documents</TabsTrigger><TabsTrigger value="file" data-testid="tab-file">4 · Save & file</TabsTrigger></TabsList>
          <TabsContent value="irn" className="card-sharp p-4"><IrnTab rows={wb.rows} period={period} reload={load} /></TabsContent>
          <TabsContent value="approve" className="card-sharp p-4"><ApproveTab rows={wb.rows} period={period} reload={load} synced={wb.portal_synced_at} /></TabsContent>
          <TabsContent value="tables" className="card-sharp p-4"><TablesTab period={period} /></TabsContent>
          <TabsContent value="file" className="card-sharp p-4"><FileTab period={period} toPush={s.to_push || 0} reload={load} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
